import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { CompanyAbout } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  aboutCompany: CompanyAbout | null;
}

const AboutCompany: React.FC<Props> = ({ aboutCompany }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <Typography className={classes.boldText}>{t('aboutCompany')}</Typography>
      <Stack direction={'row'} gap={2}>
        <Box className={classes.infoContainer} sx={{ flex: 1 }}>
          <div className={classes.infoContainerHeader}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <HomeIcon className={classes.icon} />
              <Typography className={classes.boldText}>{t('companyChannels')}</Typography>
            </Stack>
          </div>
          <Stack className={classes.infoContainerContent} gap={1}>
            <Typography className={classes.boldText}>{aboutCompany?.companyDetails.website || '-'}</Typography>
          </Stack>
        </Box>
        <Box className={classes.infoContainer} sx={{ flex: 1 }}>
          <div className={classes.infoContainerHeader}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <BusinessIcon className={classes.icon} />
              <Typography className={classes.boldText}>{t('companyInfo')}</Typography>
            </Stack>
          </div>
          <Stack className={classes.infoContainerContent} gap={1}>
            <Stack gap={0.5}>
              <Typography className={classes.greyText}>{t('noOfEmployees')}</Typography>
              <PaiListItem text={aboutCompany?.companyDetails.employeeCount || '-'} size={'medium'} disablePadding={true} />
            </Stack>
            <Divider />
            <Stack gap={0.5}>
              <Typography className={classes.greyText}>{t('headquarter')}</Typography>
              <PaiListItem text={aboutCompany?.companyDetails.headquarters || '-'} size={'medium'} disablePadding={true} />
            </Stack>
            <Divider />
            <Stack gap={0.5}>
              <Typography className={classes.greyText}>{t('industry')}</Typography>
              <PaiListItem text={aboutCompany?.companyDetails.industry || '-'} size={'medium'} disablePadding={true} />
            </Stack>
          </Stack>
        </Box>
      </Stack>
      <div className={classes.infoContainer}>
        <div className={classes.infoContainerHeader}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <DescriptionIcon className={classes.icon} />
            <Typography className={classes.boldText}>{t('companyDescription')}</Typography>
          </Stack>
        </div>
        <Stack className={classes.infoContainerContent} gap={1}>
          <Typography className={classes.text} sx={{ whiteSpace: 'pre-wrap' }}>
            {aboutCompany?.description}
          </Typography>
        </Stack>
      </div>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  boldText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
  },
  infoContainer: {
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '8px',
  },
  infoContainerHeader: {
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  infoContainerContent: {
    padding: '8px 16px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
  },
}));

export default AboutCompany;
